/**
 * KBCategoryService
 * Self-referencing KB category tree.
 * Route: api/kb-categories
 */
import { BaseService } from '../base/BaseService';
/** Default empty audience used when the API returns nothing parseable. */
const EMPTY_AUDIENCE = { roleNames: [], ministryIds: [], departmentIds: [] };
export class KBCategoryService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? KBCategoryService.DEFAULT_SERVICE_PATH,
            basePath: KBCategoryService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    async getAll() {
        const response = await this.getAllRaw();
        return response.data ?? [];
    }
    getAllRaw() {
        return this.http.get(this.buildUrl(''));
    }
    async getById(id) {
        const response = await this.getByIdRaw(id);
        return response.data ?? null;
    }
    getByIdRaw(id) {
        return this.http.get(this.buildUrl(`${id}`));
    }
    async getByKnowledgeBase(kbId) {
        const response = await this.getByKnowledgeBaseRaw(kbId);
        return response.data ?? [];
    }
    getByKnowledgeBaseRaw(kbId) {
        return this.http.get(this.buildUrl(`by-knowledge-base/${kbId}`));
    }
    async getByParent(parentCategoryId) {
        const response = await this.getByParentRaw(parentCategoryId);
        return response.data ?? [];
    }
    getByParentRaw(parentCategoryId) {
        return this.http.get(this.buildUrl(`by-parent/${parentCategoryId}`));
    }
    async create(item) {
        const response = await this.createRaw(item);
        return response.data ?? null;
    }
    createRaw(item) {
        return this.http.post(this.buildUrl(''), item);
    }
    async update(id, item) {
        const response = await this.updateRaw(id, item);
        return response.data ?? null;
    }
    updateRaw(id, item) {
        return this.http.put(this.buildUrl(`${id}`), item);
    }
    async delete(id) {
        const response = await this.deleteRaw(id);
        return response.success;
    }
    deleteRaw(id) {
        return this.http.delete(this.buildUrl(`${id}`));
    }
    // ============================================================
    // Visibility — audience management (Restricted categories only)
    // ============================================================
    /**
     * Read the role/ministry/department audience grants for a Restricted
     * KB category. Empty lists == visible to all signed-in users.
     */
    async getAudience(kbCategoryId) {
        const response = await this.getAudienceRaw(kbCategoryId);
        return response.data ?? { ...EMPTY_AUDIENCE };
    }
    getAudienceRaw(kbCategoryId) {
        return this.http.get(this.buildUrl(`${kbCategoryId}/audience`));
    }
    /** Wholesale replace a KB category's audience grants. */
    async setAudience(kbCategoryId, audience) {
        const response = await this.setAudienceRaw(kbCategoryId, audience);
        return response.data ?? audience;
    }
    setAudienceRaw(kbCategoryId, audience) {
        return this.http.put(this.buildUrl(`${kbCategoryId}/audience`), audience);
    }
}
KBCategoryService.DEFAULT_SERVICE_PATH = 'resources';
KBCategoryService.BASE_PATH = 'api/kb-categories';
//# sourceMappingURL=KBCategoryService.js.map